import {isFeatureEnabled} from 'feature-flags';
import {showNotification} from 'platform/components';

import {isNil, isNotNil} from 'ramda';

import {useGetTenantQuery, useLazyReadCodelistQuery} from '@dms/api/core';
import {CreateInterestApiArg, useCreateInterestMutation} from '@dms/api/finance';
import {useCreateEmptyPriceReportRequestMutation} from '@dms/api/sales';
import {MenuItemProps} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {interestRoutes} from '@dms/routes';
import {handleApiError, DEFAULT_COUNTRY, usePermissions} from '@dms/shared';

import {useNavigate, composePath, buildArray, openInNewTab} from 'shared/web';

import {BULK_PRICE_REPORT_MENU_PATH, getBulkPriceReportUrl} from '../utils/getBulkPriceReportUrl';

export function useCreateCustomMenuActions() {
  const navigate = useNavigate();
  const {data: tenant} = useGetTenantQuery();
  const tenantCountry = tenant?.country ?? DEFAULT_COUNTRY;

  const [createInterest] = useCreateInterestMutation();
  const [getInterestSourcesCodelist] = useLazyReadCodelistQuery();
  const [createPriceReport] = useCreateEmptyPriceReportRequestMutation();

  const [canReadBusinessCase, canCreateVehiclePriceReport] = usePermissions({
    permissionKeys: ['businessCaseRead', 'createVehiclePriceReport'],
  });

  const isSalesModuleEnabled = canReadBusinessCase;

  const bulkPriceReportUrl = getBulkPriceReportUrl(tenant?.id);

  const openBulkPriceReportAction = () => {
    if (isNil(bulkPriceReportUrl)) {
      return;
    }

    openInNewTab(bulkPriceReportUrl);
  };

  const createPriceReportAction = () => {
    createPriceReport({
      createPriceReportRequestRequestBody: {country: tenantCountry},
    })
      .unwrap()
      .then((data) => {
        window.open(data.url, '_blank');
      })
      .catch(handleApiError);
  };

  const createInterestAction = async () => {
    const codes = await getInterestSourcesCodelist({codelistId: 'interest_source'});

    const activeCodes = codes?.data?.codes.filter((code) => !code.isDisabled);

    const args: CreateInterestApiArg = {
      createInterestRequestBody: {
        customerId: null,
        sourceCodeId: activeCodes?.[0]?.codeId ?? '',
      },
    };

    await createInterest(args)
      .unwrap()
      .then((interest) => {
        if (!interest?.id) {
          showNotification.error();
          return;
        }

        navigate(
          composePath(interestRoutes.detailBuying, {
            params: {
              id: interest.id,
            },
          })
        );
      })
      .catch(handleApiError);
  };

  const createCustomMenuFastAccessItems = buildArray<MenuItemProps>()
    .when(isSalesModuleEnabled && isFeatureEnabled(featureFlags.SALES_FAST_BUYING), {
      id: 'create-fast-buying-interest',
      path: interestRoutes.create,
      layoutProps: {
        title: i18n.t('entity.interest.actions.createFastBuying'),
        isPartOfCreateMenuItem: true,
        position: 2,
      },
      action: createInterestAction,
    })
    .when(isSalesModuleEnabled && isFeatureEnabled(featureFlags.SALES_FAST_PRICING), {
      id: 'create-fast-buying-price-report',
      path: interestRoutes.create,
      layoutProps: {
        title: i18n.t('entity.priceReport.actions.createFastPriceReport'),
        isPartOfCreateMenuItem: true,
        position: 1,
      },
      action: createPriceReportAction,
    });

  const createCustomMenuActions = buildArray<MenuItemProps>()
    .when(isSalesModuleEnabled && isFeatureEnabled(featureFlags.SALES_FAST_PRICING), {
      id: 'create-fast-buying-price-report',
      path: interestRoutes.create,
      layoutProps: {
        title: i18n.t('entity.priceReport.actions.createFastPriceReport'),
        icon: 'maps/local_offer' as const,
      },
      action: createPriceReportAction,
    })
    .when(canCreateVehiclePriceReport && isNotNil(bulkPriceReportUrl), {
      id: 'open-bulk-price-report',
      path: BULK_PRICE_REPORT_MENU_PATH,
      layoutProps: {
        title: i18n.t('entity.priceReport.actions.openBulkPriceReport'),
        icon: 'sidebar/bulk_price_report' as const,
      },
      action: openBulkPriceReportAction,
    });

  return {createCustomMenuFastAccessItems, createCustomMenuActions};
}
